import {
    createContext,
    lazy,
    useContext,
    useMemo,
    useRef,
    type CSSProperties,
    type FC,
    type ReactNode,
    type RefObject,
} from 'react';
// eslint-disable-next-line import/no-unresolved
import { MediaController, MediaPosterImage } from 'media-chrome/react';
import { type MediaController as MediaControllerType } from 'media-chrome';

import { FormattedMessage } from 'dibs-react-intl';
import { ClientSuspense } from 'dibs-elements/exports/ClientSuspense.tsx';
import { Spinner } from 'dibs-elements/exports/Spinner.tsx';

import classnames from 'classnames';
import dibsCss from 'dibs-css';

const Controls = lazy(() =>
    import(/* webpackChunkName: "VideoControls" */ './Controls.tsx').then(module => ({
        default: module.Controls,
    }))
);

const Logo = lazy(() =>
    import(/* webpackChunkName: "VideoLogo" */ './Logo.tsx').then(module => ({
        default: module.Logo,
    }))
);

const Media = lazy(() =>
    import(/* webpackChunkName: "VideoMedia" */ './Media.tsx').then(module => ({
        default: module.Media,
    }))
);

const PlayButton = lazy(() =>
    import(/* webpackChunkName: "VideoPlayButton" */ './PlayButton.tsx').then(module => ({
        default: module.PlayButton,
    }))
);

type Props = {
    children: ReactNode;
    ref?: RefObject<HTMLVideoElement | null>;
    forceMute?: boolean;
    dataTn?: string;
    poster?: string;
    fallback?: ReactNode;
    width?: number | null;
    height?: number | null;
};

type VideoPlayerType = {
    Controls: typeof Controls;
    Logo: typeof Logo;
    Media: typeof Media;
    PlayButton: typeof PlayButton;
};

type ContextType = {
    playerRef?: RefObject<HTMLVideoElement | null>;
    wrapperRef?: RefObject<MediaControllerType | null>;
    forceMute?: boolean;
    dataTn?: string;
    fallback?: ReactNode;
    width?: number | null;
    height?: number | null;
};

const VideoPlayerContext = createContext<ContextType | null>(null);

export const useVideoPlayer = (): ContextType => {
    const context = useContext(VideoPlayerContext);

    if (!context) {
        throw new Error('useVideoPlayer must be used within a <VideoPlayer />');
    }

    return context;
};

export const VideoPlayer: FC<Props> & VideoPlayerType = ({
    children,
    ref,
    forceMute = true,
    dataTn,
    poster,
    fallback,
    width,
    height,
}) => {
    const wrapperRef = useRef<MediaControllerType | null>(null);
    const aspectRatio = width && height ? width / height : undefined;

    const fallbackComponent = useMemo(() => {
        if (fallback) return fallback;
        return (
            <div
                className={classnames(
                    dibsCss.wFull,
                    dibsCss.hFull,
                    dibsCss.flex,
                    dibsCss.itemsCenter,
                    dibsCss.justifyCenter,
                    dibsCss.flexCol
                )}
                style={{ aspectRatio }}
            >
                <Spinner black size="small" id="dibs-video.player-spinner" />
                <span
                    className={classnames(dibsCss.mtSmall, dibsCss.sassyFontSubHeaderMediumNormal)}
                >
                    <FormattedMessage id="dibs-video.loadingVideo" defaultMessage="Video Loading" />
                </span>
            </div>
        );
    }, [aspectRatio, fallback]);

    return (
        <VideoPlayerContext.Provider
            value={{
                playerRef: ref,
                forceMute,
                dataTn,
                fallback: fallbackComponent,
                wrapperRef,
                width,
                height,
            }}
        >
            <ClientSuspense fallback={fallbackComponent}>
                <MediaController
                    ref={wrapperRef}
                    className={classnames(dibsCss.hFull, dibsCss.bgTransparent, {
                        [dibsCss.wFull]: !aspectRatio,
                        [dibsCss.maxWFull]: !!aspectRatio,
                    })}
                    style={{ aspectRatio }}
                >
                    {children}
                    {poster && (
                        <MediaPosterImage
                            slot="poster"
                            src={poster}
                            style={{ '--media-object-fit': 'cover' } as CSSProperties}
                        />
                    )}
                </MediaController>
            </ClientSuspense>
        </VideoPlayerContext.Provider>
    );
};

VideoPlayer.Controls = Controls;
VideoPlayer.Logo = Logo;
VideoPlayer.Media = Media;
VideoPlayer.PlayButton = PlayButton;
